// Pay & Hours — client types + fetch for GET /api/v1/pay-hours?location=<CO>
// (src/pay_hours.js). One clinic's roster, rate + rate history, hours since
// the last raise, next-raise status, and the annual bonus window — computed
// server-side from register lines, People, and owner rulings only (owner,
// 2026-09-08: "driven from very few points of information ... nothing
// copied"). This file adds no computation of its own beyond formatting; the
// numbers and states are exactly what the server sends.
import { authedGet } from './apiClient';
import type { LabelledPayPeriod } from './payPeriodLabel';
import type { RaiseDecisionInfo } from './hourRaises';
import type { LocationCode } from './locations';

export type RaiseStatus = 'crossed' | 'due' | 'moved' | 'rejected';

export interface RaiseInfo {
  status: RaiseStatus;
  hoursSinceRaise: number;
  towardNext: number;
  hoursToGo: number;
  milestones: number;
  nextRaise: LabelledPayPeriod | null;
  decision: (RaiseDecisionInfo & { id: number; employeeId: number }) | null;
}

/** The 1,200-hour bonus (owner, 2026-10-06): checked AT the anniversary, not
 * when 1,200 is crossed. `reached`/`earnedAmount` = this year so far, payable
 * at `windowEnd`; `bonusDue`/`bonusAmount`/`paidWeek` = the year that ended
 * at `windowStart`. */
export interface AnniversaryInfo {
  windowStart: string;
  windowEnd: string;
  hoursInWindow: number;
  reached?: boolean;
  earnedAmount?: number | null;
  lastYearHours?: number | null;
  bonusDue: boolean;
  /** Due only if some hours in the payroll holding the anniversary were
   * worked before it — register hours are per payroll, so this cannot be
   * proven (owner, 2026-10-15, #49: that payroll counts toward the new year). */
  bonusUnproven?: boolean;
  anniversaryPayrollHours?: number | null;
  bonusAmount: number | null;
  paidWeek: LabelledPayPeriod | null;
  /** Bonuses in the window that are NOT the 1,200-hour one (not 12x the
   * rate, not labelled "1,200-hour bonus") — e.g. a hiring bonus. Never
   * counted as paying it (owner, 2026-10-10). */
  otherBonuses?: (LabelledPayPeriod & { amount: number | null })[];
}

export interface TerminationVerdict {
  code: 'PAID_NEXT_CYCLE' | 'NO_PAY_NOW' | 'NOT_IMPORTED';
  label: string;
}

export interface PayHoursRow {
  employeeId: number;
  fileNo: string | null;
  fullName: string;
  role: string;
  hireDate: string | null;
  rateNow: number | null;
  rateType: string | null;
  effectiveSince: string | null;
  raisedFrom: number | null;
  hoursSinceRaise: number | null;
  raise: RaiseInfo | null;
  anniversary: AnniversaryInfo | null;
  termination: TerminationVerdict | null;
  lastPaid: LabelledPayPeriod | null;
}

export interface PayHoursData {
  locationCode: string;
  rows: PayHoursRow[];
  following: LabelledPayPeriod | null;
  raiseAt: number;
  bonusAt: number;
}

export function payHoursKey(locationCode: LocationCode): string {
  return `pay-hours:${locationCode}:v1`;
}

export function fetchPayHours(locationCode: LocationCode): Promise<PayHoursData> {
  return authedGet<PayHoursData>(`/api/v1/pay-hours?location=${locationCode}`);
}

/** Counts for the Desk card — "N raises due, M bonuses due" — never asserted
 * in prose without being counted from the data (standing rule 9). Only
 * `crossed` and `due` are raises AWAITING action; `rejected` and `moved` are
 * already decided and stay out of the count the same way a decided alert
 * drops off Home's RaiseAlerts queue. */
export function countsFor(data: PayHoursData | null | undefined): { raisesDue: number; bonusesDue: number } {
  const rows = data?.rows ?? [];
  let raisesDue = 0;
  let bonusesDue = 0;
  for (const r of rows) {
    if (r.raise && (r.raise.status === 'crossed' || r.raise.status === 'due')) raisesDue += 1;
    if (r.anniversary?.bonusDue && !r.anniversary.paidWeek) bonusesDue += 1;
  }
  return { raisesDue, bonusesDue };
}
